import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Crown, Loader2, Check, AlertCircle } from "lucide-react";

export default function UpgradeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | success | pending | expired | error
  const [attempts, setAttempts] = useState(0);
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        // New subscription endpoint first; fall back to legacy one-time checkout.
        const { data } = await api.get(`/billing/subscribe/status/${sessionId}`)
          .catch(() => api.get(`/billing/status/${sessionId}`).then((r) => r));
        if (data.payment_status === "paid") {
          // refresh /auth/me to get is_premium = true
          try {
            const { data: me } = await api.get("/auth/me");
            await updateProfile({}); // triggers state refresh through context
            // Manually update local storage with new user data
            localStorage.setItem("ws_user", JSON.stringify(me));
          } catch {}
          setStatus("success");
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (n >= 8) { setStatus("pending"); return; }
        setAttempts(n);
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        if (n >= 8) { setStatus("error"); return; }
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  if (status === "checking") {
    return (
      <div className="max-w-xl mx-auto py-16 text-center" data-testid="upgrade-checking">
        <Loader2 className="animate-spin mx-auto mb-4 text-indigo-600" size={36}/>
        <h1 className="text-2xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Confirming your payment…</h1>
        <p className="text-slate-600">Attempt {attempts + 1} of 9. Hang tight.</p>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="max-w-xl mx-auto py-16" data-testid="upgrade-success">
        <div className="tactile-card p-10 text-center bg-gradient-to-br from-amber-50 to-rose-50 border-amber-100">
          <div className="w-16 h-16 rounded-full bg-amber-400 text-slate-900 grid place-items-center mx-auto mb-4">
            <Crown size={28}/>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Welcome to Premium 🎉</h1>
          <p className="text-slate-700 mb-6">Unlimited coaching unlocked. Time to put it to work.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/app/revision" data-testid="upgrade-cta-revision" className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-6 py-3 tactile-shadow">
              Try Revision Intelligence
            </Link>
            <Link to="/app/paths" className="bg-white border-2 border-slate-200 text-slate-900 font-bold rounded-full px-6 py-3 hover:border-slate-300">
              See Growth Paths
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="max-w-xl mx-auto py-16 text-center" data-testid="upgrade-pending">
        <Loader2 className="animate-spin mx-auto mb-4 text-slate-400" size={36}/>
        <h1 className="text-2xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Still processing</h1>
        <p className="text-slate-600 mb-4">Stripe is taking a little longer than usual. Refresh in a minute, or check your email for confirmation.</p>
        <button onClick={()=>window.location.reload()} className="bg-indigo-600 text-white font-bold rounded-full px-6 py-2.5">Refresh</button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-16 text-center" data-testid="upgrade-error">
      <AlertCircle className="mx-auto mb-4 text-rose-500" size={36}/>
      <h1 className="text-2xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Something went sideways</h1>
      <p className="text-slate-600 mb-4">We couldn't confirm your payment. If you were charged, please contact <a href="mailto:info@writersspark.net" className="text-indigo-700 font-semibold">info@writersspark.net</a>.</p>
      <button onClick={()=>navigate("/app/upgrade")} className="bg-indigo-600 text-white font-bold rounded-full px-6 py-2.5">Back to Upgrade</button>
    </div>
  );
}
